import { API_BASE_URL } from '../config/api.config';
import api from './api.interceptor';
import { Vacancy, VacancyFilters, VacancyListResponse, VacancyCreateData } from '../types/VacancyTypes';
import { ReferralSubmission, ReferralSubmitData } from '../types/ReferralTypes';
import { mockVacancies } from '../data/mockVacancies';
import { autoLabel } from '../utils/autoLabel';

const USE_MOCK = process.env.NODE_ENV === 'development';

// Scraped vacancies loaded once from static JSON
let scrapedVacancies: Vacancy[] = [];
let scrapedLoaded = false;

async function loadScrapedVacancies(): Promise<Vacancy[]> {
  if (scrapedLoaded) return scrapedVacancies;
  try {
    const resp = await fetch('/data/scraped_vacancies.json');
    if (resp.ok) {
      scrapedVacancies = await resp.json();
    }
  } catch {
    // scraped data not available, continue with mock only
  }
  scrapedLoaded = true;
  return scrapedVacancies;
}

function tagVacancy(v: Vacancy): Vacancy {
  if (v.tags) return v;
  const labels = autoLabel(v);
  return { ...v, tags: labels };
}

function getAllVacancies(scraped: Vacancy[]): Vacancy[] {
  // Manual (mock) vacancies first, then scraped — all auto-labeled
  return [...mockVacancies, ...scraped].map(tagVacancy);
}

class VacanciesService {
  private baseUrl = `${API_BASE_URL}/vacancies`;

  async getVacancies(
    filters: VacancyFilters,
    page: number = 1,
    pageSize: number = 20
  ): Promise<VacancyListResponse> {
    if (USE_MOCK) {
      const scraped = await loadScrapedVacancies();
      return this.getMockVacancies(filters, page, pageSize, scraped);
    }

    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.minBounty != null) params.append('min_bounty', String(filters.minBounty));
    if (filters.maxBounty != null) params.append('max_bounty', String(filters.maxBounty));
    if (filters.isRemote != null) params.append('is_remote', String(filters.isRemote));
    if (filters.company) params.append('company', filters.company);
    params.append('sort_by', filters.sortBy);
    params.append('sort_order', filters.sortOrder);
    params.append('page', String(page));
    params.append('page_size', String(pageSize));

    try {
      const response = await api.get(`${this.baseUrl}?${params}`, { skipAuth: true });
      if (!response.ok) throw new Error('Failed to fetch vacancies');
      return await response.json();
    } catch {
      const scraped = await loadScrapedVacancies();
      return this.getMockVacancies(filters, page, pageSize, scraped);
    }
  }

  async getVacancy(id: string): Promise<Vacancy | null> {
    if (USE_MOCK) {
      const scraped = await loadScrapedVacancies();
      return getAllVacancies(scraped).find(v => v.id === id) || null;
    }

    try {
      const response = await api.get(`${this.baseUrl}/${id}`, { skipAuth: true });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      const scraped = await loadScrapedVacancies();
      return getAllVacancies(scraped).find(v => v.id === id) || null;
    }
  }

  async createVacancy(data: VacancyCreateData, isVerifiedPoster: boolean = false): Promise<Vacancy> {
    if (USE_MOCK) {
      const vacancy: Vacancy = {
        id: `v_${Date.now()}`,
        ...data,
        posted_by: 'mock-user',
        posted_by_name: 'Dev User',
        is_verified_poster: isVerifiedPoster,
        status: 'open',
        referral_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      // Add to mock data at the beginning
      mockVacancies.unshift(vacancy);
      return vacancy;
    }

    const response = await api.post(this.baseUrl, data);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create vacancy');
    }
    return await response.json();
  }

  async submitReferral(vacancyId: string, data: ReferralSubmitData): Promise<ReferralSubmission> {
    if (USE_MOCK) {
      const scraped = await loadScrapedVacancies();
      const vacancy = getAllVacancies(scraped).find(v => v.id === vacancyId);
      const isSelfApply = data.type === 'self_apply';
      const submission: ReferralSubmission = {
        id: `ref_${Date.now()}`,
        vacancy_id: vacancyId,
        vacancy_title: vacancy?.title || '',
        company_name: vacancy?.company_name || '',
        referrer_id: isSelfApply ? 'self' : 'mock-user',
        referrer_name: isSelfApply ? data.candidate_name : 'Dev User',
        candidate_name: data.candidate_name,
        candidate_email: data.candidate_email,
        candidate_linkedin: data.candidate_linkedin,
        note: data.note,
        type: data.type || 'referral',
        ...(data.candidate_profile && { candidate_profile: data.candidate_profile }),
        status: 'submitted',
        bounty_amount: vacancy?.bounty_amount || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      // Increment referral count
      if (vacancy) vacancy.referral_count++;
      // Persist in localStorage
      const stored = JSON.parse(localStorage.getItem('my_referrals') || '[]');
      stored.unshift(submission);
      localStorage.setItem('my_referrals', JSON.stringify(stored));
      return submission;
    }

    const response = await api.post(`${this.baseUrl}/${vacancyId}/refer`, data);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit referral');
    }
    return await response.json();
  }

  async getMyReferrals(): Promise<ReferralSubmission[]> {
    if (USE_MOCK) {
      const stored = localStorage.getItem('my_referrals');
      return stored ? JSON.parse(stored) : [];
    }

    try {
      const response = await api.get(`${this.baseUrl}/referrals/mine`);
      if (!response.ok) return [];
      return await response.json();
    } catch {
      return [];
    }
  }

  /** Get unique company names from all vacancies (for company filter). */
  async getCompanyNames(): Promise<string[]> {
    const scraped = await loadScrapedVacancies();
    const all = getAllVacancies(scraped);
    const names = Array.from(new Set(all.map(v => v.company_name)));
    names.sort();
    return names;
  }

  /** Get aggregated tag counts for sidebar facets. */
  async getTagCounts(): Promise<{
    departments: Record<string, number>;
    seniorities: Record<string, number>;
    workStyles: Record<string, number>;
    companies: Record<string, number>;
  }> {
    const scraped = await loadScrapedVacancies();
    const all = getAllVacancies(scraped);
    const departments: Record<string, number> = {};
    const seniorities: Record<string, number> = {};
    const workStyles: Record<string, number> = {};
    const companies: Record<string, number> = {};
    for (const v of all) {
      const d = v.tags?.department || 'Other';
      const s = v.tags?.seniority || 'Mid';
      const w = v.tags?.workStyle || 'On-site';
      departments[d] = (departments[d] || 0) + 1;
      seniorities[s] = (seniorities[s] || 0) + 1;
      workStyles[w] = (workStyles[w] || 0) + 1;
      companies[v.company_name] = (companies[v.company_name] || 0) + 1;
    }
    return { departments, seniorities, workStyles, companies };
  }

  // --- Mock helpers ---

  private getMockVacancies(
    filters: VacancyFilters,
    page: number,
    pageSize: number,
    scraped: Vacancy[]
  ): VacancyListResponse {
    let filtered = getAllVacancies(scraped);

    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter(
        v =>
          v.title.toLowerCase().includes(q) ||
          v.company_name.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q)
      );
    }
    if (filters.company) {
      filtered = filtered.filter(v => v.company_name === filters.company);
    }
    if (filters.isRemote != null) {
      filtered = filtered.filter(v => v.is_remote === filters.isRemote);
    }
    if (filters.minBounty != null) {
      filtered = filtered.filter(v => v.bounty_amount >= filters.minBounty!);
    }
    if (filters.maxBounty != null) {
      filtered = filtered.filter(v => v.bounty_amount <= filters.maxBounty!);
    }
    if (filters.departments && filters.departments.length > 0) {
      filtered = filtered.filter(v => filters.departments!.includes(v.tags?.department || ''));
    }
    if (filters.seniorities && filters.seniorities.length > 0) {
      filtered = filtered.filter(v => filters.seniorities!.includes(v.tags?.seniority || ''));
    }
    if (filters.workStyles && filters.workStyles.length > 0) {
      filtered = filtered.filter(v => filters.workStyles!.includes(v.tags?.workStyle || ''));
    }

    // Sort
    filtered.sort((a, b) => {
      const key = filters.sortBy;
      const aVal = key === 'bounty_amount' ? a.bounty_amount : key === 'title' ? a.title : a.created_at;
      const bVal = key === 'bounty_amount' ? b.bounty_amount : key === 'title' ? b.title : b.created_at;
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return filters.sortOrder === 'asc' ? cmp : -cmp;
    });

    const total = filtered.length;
    const start = (page - 1) * pageSize;
    const vacancies = filtered.slice(start, start + pageSize);

    return { vacancies, total, page, page_size: pageSize };
  }
}

export const vacanciesService = new VacanciesService();
export default vacanciesService;
